// The one React island: the audio player on the episode page. Everything
// else in the app is server-rendered Rails; this exists because a player is
// genuinely stateful (scrubbing, buffering, a speed menu, a sync loop) and
// that state has no business being spread across a dozen Stimulus targets.
//
// Position sync: PUT /api/v1/positions with the feed guid and a client
// timestamp, last-writer-wins on the server. Writes go out every 10 seconds
// while playing, plus on pause, seek, rate change, ended, and page hide.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createRoot } from "react-dom/client"

const SPEEDS = [0.75, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3]
const SYNC_EVERY_MS = 10_000
const SPEED_KEY = "earshot:speed"

function formatTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) sec = 0
  const total = Math.floor(sec)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m)
  return (h > 0 ? `${h}:${mm}` : mm) + ":" + String(s).padStart(2, "0")
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content
}

function Player({ episode, initial, adSpans, endpoints }) {
  const audioRef = useRef(null)
  const seekRef = useRef(null)
  const lastSyncRef = useRef(0)
  const scrubValueRef = useRef(null)

  const [playing, setPlaying] = useState(false)
  const [position, setPosition] = useState(initial.positionSec || 0)
  const [duration, setDuration] = useState(episode.durationSec || 0)
  const [buffered, setBuffered] = useState(0)
  const [scrubbing, setScrubbing] = useState(false)
  const [scrubValue, setScrubValue] = useState(null)
  const [speed, setSpeed] = useState(() => {
    const saved = parseFloat(localStorage.getItem(SPEED_KEY))
    return SPEEDS.includes(saved) ? saved : 1
  })
  const [speedOpen, setSpeedOpen] = useState(false)
  const [status, setStatus] = useState("")

  const shown = scrubbing && scrubValue != null ? scrubValue : position
  const pct = duration > 0 ? Math.min(100, (100 * shown) / duration) : 0
  const bufferedPct = duration > 0 ? Math.min(100, (100 * buffered) / duration) : 0

  const syncPosition = useCallback((positionSec, { played = null, keepalive = false } = {}) => {
    const body = {
      guid: episode.guid,
      feed_url: episode.feedUrl,
      position_sec: positionSec,
      duration_sec: audioRef.current?.duration || episode.durationSec || null,
      updated_at_ms: Date.now(),
      device: "web"
    }
    if (played !== null) body.played = played
    lastSyncRef.current = Date.now()
    fetch(endpoints.positions, {
      method: "PUT",
      keepalive,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify(body)
    })
      .then((r) => {
        if (r.ok) {
          setStatus("Position synced")
          setTimeout(() => setStatus(""), 1600)
        }
      })
      .catch(() => {})
  }, [episode, endpoints])

  // Wire the audio element once.
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onTime = () => {
      if (!scrubValueRef.current) setPosition(audio.currentTime)
      if (audio.buffered.length > 0) setBuffered(audio.buffered.end(audio.buffered.length - 1))
      if (Date.now() - lastSyncRef.current > SYNC_EVERY_MS && !audio.paused) {
        syncPosition(audio.currentTime)
      }
    }
    const onLoaded = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration)
      // A ?t= deep link (a transcript search hit) beats the stored
      // position; otherwise resume where any device left off, but restart
      // a finished episode.
      if (Number.isFinite(initial.startAtSec)) {
        audio.currentTime = initial.startAtSec
        setPosition(initial.startAtSec)
      } else if (initial.positionSec > 0 && !initial.played) {
        audio.currentTime = initial.positionSec
      }
      // Queue advance and Play links land with autoplay=1. Browsers may
      // veto a play() with no gesture behind it; failing quietly leaves an
      // ordinary paused player.
      if (initial.autoplay) audio.play().catch(() => {})
    }
    const onPlay = () => setPlaying(true)
    const onPause = () => {
      setPlaying(false)
      syncPosition(audio.currentTime)
    }
    const onEnded = () => {
      setPlaying(false)
      syncPosition(audio.duration || audio.currentTime, { played: true })
      setStatus("Finished")
      // Walk Up Next: pop this episode from the queue server-side and go
      // wherever the queue says next, the way the iOS app auto-advances.
      if (!endpoints.queueAdvance) return
      fetch(endpoints.queueAdvance, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ guid: episode.guid, feed_url: episode.feedUrl })
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.next_url) {
            setStatus(`Next: ${data.next_title || "next episode"}`)
            window.location.assign(data.next_url)
          }
        })
        .catch(() => {})
    }

    audio.addEventListener("timeupdate", onTime)
    audio.addEventListener("loadedmetadata", onLoaded)
    audio.addEventListener("play", onPlay)
    audio.addEventListener("pause", onPause)
    audio.addEventListener("ended", onEnded)
    audio.playbackRate = speed
    return () => {
      audio.removeEventListener("timeupdate", onTime)
      audio.removeEventListener("loadedmetadata", onLoaded)
      audio.removeEventListener("play", onPlay)
      audio.removeEventListener("pause", onPause)
      audio.removeEventListener("ended", onEnded)
    }
  }, [syncPosition])  // eslint-disable-line react-hooks/exhaustive-deps

  // A final write when the tab goes away mid-listen.
  useEffect(() => {
    const onHide = () => {
      const audio = audioRef.current
      if (audio && audio.currentTime > 0 && !audio.paused) {
        syncPosition(audio.currentTime, { keepalive: true })
      }
    }
    window.addEventListener("pagehide", onHide)
    return () => window.removeEventListener("pagehide", onHide)
  }, [syncPosition])

  // Transcript timestamps live outside the island; they dispatch clicks on
  // [data-seek-sec] and the player answers.
  useEffect(() => {
    const onClick = (event) => {
      const target = event.target.closest("[data-seek-sec]")
      if (!target) return
      const sec = parseFloat(target.dataset.seekSec)
      if (!Number.isFinite(sec)) return
      seekTo(sec, { andPlay: true })
    }
    document.addEventListener("click", onClick)
    return () => document.removeEventListener("click", onClick)
  })

  const seekTo = (sec, { andPlay = false } = {}) => {
    const audio = audioRef.current
    if (!audio) return
    const clamped = Math.max(0, Math.min(sec, duration || sec))
    audio.currentTime = clamped
    setPosition(clamped)
    syncPosition(clamped)
    if (andPlay && audio.paused) audio.play()
  }

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) audio.play()
    else audio.pause()
  }

  const skip = (delta) => seekTo((audioRef.current?.currentTime || 0) + delta)

  const chooseSpeed = (value) => {
    setSpeed(value)
    setSpeedOpen(false)
    localStorage.setItem(SPEED_KEY, String(value))
    if (audioRef.current) audioRef.current.playbackRate = value
  }

  // Scrubbing with pointer events on the whole seek strip.
  const scrubFromEvent = (event) => {
    const rect = seekRef.current.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    return ratio * (duration || 0)
  }

  const onPointerDown = (event) => {
    if (!duration) return
    event.currentTarget.setPointerCapture(event.pointerId)
    const value = scrubFromEvent(event)
    scrubValueRef.current = value
    setScrubbing(true)
    setScrubValue(value)
  }
  const onPointerMove = (event) => {
    if (!scrubbing) return
    const value = scrubFromEvent(event)
    scrubValueRef.current = value
    setScrubValue(value)
  }
  const onPointerUp = () => {
    if (!scrubbing) return
    const value = scrubValueRef.current
    scrubValueRef.current = null
    setScrubbing(false)
    setScrubValue(null)
    if (value != null) seekTo(value)
  }

  const onSeekKey = (event) => {
    if (event.key === "ArrowLeft") { event.preventDefault(); skip(-15) }
    if (event.key === "ArrowRight") { event.preventDefault(); skip(30) }
    if (event.key === " " || event.key === "Enter") { event.preventDefault(); togglePlay() }
  }

  const remaining = (duration || 0) - shown

  return (
    <div className="player-card">
      <audio ref={audioRef} src={episode.audioUrl} preload="metadata" />
      {episode.artworkUrl
        ? <img className="player-art" src={episode.artworkUrl} alt="" />
        : <div className="player-art artwork-placeholder">{episode.showTitle.slice(0, 2).toUpperCase()}</div>}

      <div className="player-body">
        <p className="player-title">{episode.title}</p>
        <p className="player-show">{episode.showTitle}</p>

        <div
          className="seek-wrap"
          ref={seekRef}
          role="slider"
          tabIndex={0}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration || 0)}
          aria-valuenow={Math.round(shown)}
          aria-valuetext={formatTime(shown)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onKeyDown={onSeekKey}
        >
          <div className="seek-track">
            <span className="seek-buffered" style={{ width: `${bufferedPct}%` }} />
            {duration > 0 && adSpans.map((span, i) => (
              <span
                key={i}
                className="seek-ad"
                title="Ad"
                style={{
                  left: `${(100 * span.startSec) / duration}%`,
                  width: `${(100 * (span.endSec - span.startSec)) / duration}%`
                }}
              />
            ))}
            <span className="seek-played" style={{ width: `${pct}%` }} />
          </div>
          <span className="seek-thumb" style={{ left: `${pct}%` }} />
        </div>

        <div className="player-times">
          <span>{formatTime(shown)}</span>
          <span>-{formatTime(remaining)}</span>
        </div>

        <div className="player-controls">
          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="speed-btn"
              aria-haspopup="listbox"
              aria-expanded={speedOpen}
              aria-label={`Playback speed, currently ${speed}x`}
              onClick={() => setSpeedOpen((open) => !open)}
            >
              {speed}&times;
            </button>
            {speedOpen && (
              <ul className="speed-menu" role="listbox" aria-label="Playback speed"
                  style={{ bottom: "2.7rem", left: 0 }}>
                {SPEEDS.map((value) => (
                  <li key={value} role="none">
                    <button type="button" role="option" aria-checked={value === speed}
                            style={{ width: "100%" }}
                            onClick={() => chooseSpeed(value)}>
                      <span>{value}&times;</span>
                      {value === speed && <span aria-hidden="true">✓</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button type="button" className="ctl-btn ctl-skip" aria-label="Back 15 seconds" onClick={() => skip(-15)}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 4a8 8 0 1 1-7.6 5.5" />
              <path d="M4 4v5h5" />
            </svg>
            <span className="skip-num">15</span>
          </button>

          <button type="button" className="ctl-btn ctl-play" aria-label={playing ? "Pause" : "Play"} onClick={togglePlay}>
            {playing ? (
              <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <rect x="6" y="5" width="4.4" height="14" rx="1.4" />
                <rect x="13.6" y="5" width="4.4" height="14" rx="1.4" />
              </svg>
            ) : (
              <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5.5v13a1 1 0 0 0 1.52.86l10.2-6.5a1 1 0 0 0 0-1.7L9.52 4.63A1 1 0 0 0 8 5.5z" />
              </svg>
            )}
          </button>

          <button type="button" className="ctl-btn ctl-skip" aria-label="Forward 30 seconds" onClick={() => skip(30)}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 4a8 8 0 1 0 7.6 5.5" />
              <path d="M20 4v5h-5" />
            </svg>
            <span className="skip-num">30</span>
          </button>

          {/* Mirror of the speed button's footprint so play stays centered. */}
          <span style={{ minWidth: "3.6rem" }} aria-hidden="true"></span>
        </div>

        <p className="player-status" role="status">{status}</p>
      </div>
    </div>
  )
}

let root = null

function mountPlayer() {
  const el = document.getElementById("player-root")
  if (!el) return
  const props = JSON.parse(el.dataset.playerProps)
  root = createRoot(el)
  root.render(<Player {...props} />)
}

function unmountPlayer() {
  if (root) {
    root.unmount()
    root = null
  }
}

document.addEventListener("turbo:load", mountPlayer)
document.addEventListener("turbo:before-render", unmountPlayer)
